'use client';

import { WalletName } from '@solana/wallet-adapter-base';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import { DriftWalletName } from 'src/constants/wallets';
import { DRIFT_CUSTOM_WALLET_OPTIONS } from 'src/constants/wallets';
import { dlog } from '../dev';
import useCurrentWalletConnectionState from './useCurrentWalletConnectionState';
import useWalletContext from 'src/hooks/useWalletContext';
import { useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import usePostHogCapture from './posthog/usePostHogCapture';
import useConnectCustomWallet from './wallet-management/useConnectCustomWallet';
import { sleep } from '@drift/common';
import useSyncedRef from './utils/useSyncedRef';
import { Provider } from '@reown/appkit-adapter-solana/react';
import { useCallback } from 'react';

export const WALLET_NAME_LOCALSTORAGE_KEY = 'walletName';

const useHandleSelectWalletProvider = () => {
	const { connectWithCustomWallet } = useConnectCustomWallet();
	const walletContext = useWalletContext();
	const setDriftStore = useDriftStore((s) => s.set);
	const walletConnectionState = useCurrentWalletConnectionState();

	const { address } = useAppKitAccount();
	const appkitAddressRef = useSyncedRef(address);
	const appkitWalletProvider = useAppKitProvider<Provider>('solana');
	const appkitWalletProviderRef = useSyncedRef(appkitWalletProvider);
	const { captureEvent } = usePostHogCapture();
	const captureEventRef = useSyncedRef(captureEvent); // we use a ref here, because captureEvent as a callback is updated when the wallet just connected. this could cause a re-run of handleSelectProvider below

	const handleSelectAppKit = useCallback(async () => {
		await sleep(500); // need some time for the walletProvider to load in the appkit wallet provider

		// allow more time for appkit to initialize if needed
		if (
			!appkitAddressRef.current ||
			!appkitWalletProviderRef.current.walletProvider
		) {
			await sleep(1000);
		}

		await connectWithCustomWallet(
			DRIFT_CUSTOM_WALLET_OPTIONS.APPKIT_WALLET_ADAPTER_NAME,
			{
				provider: appkitWalletProviderRef.current.walletProvider,
				address: appkitAddressRef.current ?? '',
			}
		);
	}, [connectWithCustomWallet]);

	const handleSelectProvider = useCallback(
		async (selectedProviderName: DriftWalletName) => {
			dlog(`wallet_debugging`, `handleSelectProvider: ${selectedProviderName}`);
			walletConnectionState.instance.progress('Connecting');

			// setPreferredWalletUrl(selectedProviderName);
			window.localStorage.setItem(
				WALLET_NAME_LOCALSTORAGE_KEY,
				JSON.stringify(selectedProviderName)
			);
			if (
				selectedProviderName ===
				DRIFT_CUSTOM_WALLET_OPTIONS.APPKIT_WALLET_ADAPTER_NAME
			) {
				await handleSelectAppKit();
			} else {
				const provider = walletContext.wallets.find(
					(wallet) => wallet.adapter.name === selectedProviderName
				);

				try {
					setDriftStore((state) => {
						// @ts-expect-error
						state.wallet.current = provider;
						state.wallet.isMagicAuth = false;
						state.wallet.isAppKit = false;
						state.wallet.isPrivyNativeAuth = false;
					});

					walletContext.select(selectedProviderName as WalletName);
					await provider?.adapter.connect();
				} catch (e) {
					console.error(`wallet_connection_error`, e);

					captureEventRef.current('wallet_connection_error', {
						provider: provider?.adapter.name ?? '',
						error: e,
					});

					walletConnectionState.instance.progress('NotConnected');

					notify({
						type: 'error',
						message: 'There was an error connecting to the selected wallet',
						description:
							"You may need to install the wallet provider's extension",
					});
				}
			}
		},
		[
			walletContext,
			walletConnectionState.instance,
			setDriftStore,
			connectWithCustomWallet,
			handleSelectAppKit,
		]
	);

	return {
		handleSelectProvider,
	};
};

export default useHandleSelectWalletProvider;
